/**
 * Email Edit — /(app)/settings/account/email
 *
 * Allows the user to request an email address change. A confirmation link
 * is sent to the NEW address; the email does not change until the user
 * clicks the link.
 *
 * States:
 *   form    — input for new email + "Send confirmation email" button
 *   sent    — confirmation sent; show new email + "try a different address" reset
 */

import { COMPACT_HEADER_STYLE } from '../../../../lib/gameChrome'
import React, { useState } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../../hooks/useOrientation'
import { useAuth } from '../../../../hooks/useAuth'
import { apiPost, HttpError } from '../../../../lib/api'
import { validateEmail } from '../../../../lib/emailValidation'
import { goBackOr } from '../../../../lib/goBack'
import { Button } from '../../../../components/ui/Button'
import { Input } from '../../../../components/ui/Input'
import { KeyboardAwareScrollView } from '../../../../components/ui/KeyboardAwareScrollView'
import type {
  RequestEmailChangeRequest,
  RequestEmailChangeResponse,
} from '@warcarp/api-types'

type ScreenState = 'form' | 'sent'

export default function EmailEditScreen() {
  const { user } = useAuth()
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(480, winWidth * 0.55) : undefined

  const [newEmail, setNewEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [screenState, setScreenState] = useState<ScreenState>('form')
  const [sentTo, setSentTo] = useState('')

  async function handleSubmit() {
    const err = validateEmail(newEmail)
    if (err) {
      setEmailError(err)
      return
    }

    setSubmitting(true)
    setEmailError('')
    try {
      await apiPost<RequestEmailChangeResponse>(
        '/api/v1/auth/request-email-change',
        { newEmail: newEmail.trim() } satisfies RequestEmailChangeRequest,
      )
      setSentTo(newEmail.trim())
      setScreenState('sent')
    } catch (e: unknown) {
      if (e instanceof HttpError && e.status === 400) {
        setEmailError('That email address is already in use')
      } else {
        setEmailError(e instanceof Error ? e.message : 'Something went wrong — please try again')
      }
    } finally {
      setSubmitting(false)
    }
  }

  function handleReset() {
    setNewEmail('')
    setEmailError('')
    setScreenState('form')
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      {/* Header */}
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings/account')}
          accessibilityLabel="Back to account settings"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Email</Text>
      </View>

      <KeyboardAwareScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 16,
          gap: 20,
          alignItems: isLandscape ? 'center' : undefined,
        }}
        keyboardDismissMode="on-drag"
      >
          <View style={isLandscape ? { width: contentWidth, gap: 20 } : { width: '100%', gap: 20 }}>
          {/* Current email — always visible, read-only */}
          <View className="bg-paper rounded-2xl p-4 gap-1">
            <Text className="text-ink-soft text-xs font-stencil uppercase tracking-widest">
              Current Email
            </Text>
            <Text className="font-typed text-ink text-base">{user?.email ?? ''}</Text>
          </View>

          {screenState === 'form' ? (
            <View className="bg-paper rounded-2xl p-4 gap-3">
              <Text className="text-ink font-stencil uppercase">New Email</Text>
              <Input
                value={newEmail}
                onChangeText={(v) => {
                  setNewEmail(v)
                  setEmailError('')
                }}
                placeholder="you@example.com"
                keyboardType="email-address"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect={false}
                error={emailError}
              />
              <Text className="font-typed text-ink-soft text-xs italic">
                // We'll send a confirmation link to this address. Your current email stays active until you confirm the change.
              </Text>
              <Button
                title="Send confirmation email"
                onPress={handleSubmit}
                loading={submitting}
                disabled={!newEmail.trim() || submitting}
              />
            </View>
          ) : (
            <View className="bg-paper rounded-2xl p-4 gap-4">
              <Text className="font-stencil text-ink uppercase text-base">Confirmation sent</Text>
              <Text className="font-typed text-ink text-sm leading-6">
                Confirmation sent to{' '}
                <Text className="font-typed text-ink">{sentTo}</Text>.
                {' '}Check that inbox and click the link to make the change official.
                Your existing email keeps working until then.
              </Text>
              <TouchableOpacity
                onPress={handleReset}
                accessibilityLabel="Send to a different address"
                accessibilityRole="button"
              >
                <Text className="font-typed text-blood text-sm">Send to a different address →</Text>
              </TouchableOpacity>
            </View>
          )}
          </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}
